import { useCallback, useEffect, useMemo, useState } from 'react';
import { format, parse } from 'date-fns';
import { ChevronLeft, ChevronRight, Loader2, User, UserMinus, UserPlus, Users } from 'lucide-react';
import toast from 'react-hot-toast';
import type { Lesson } from '@court-sync/shared';
import { TierFeatureKey } from '@court-sync/shared';
import {
  computeCampEnrollmentByDay,
  countUniqueCampStudents,
  groupCampEnrollmentsByDay,
  isCampFullyBookedByDay,
  shouldShowEnrollmentGuardian,
} from '@court-sync/shared';
import { getCampEnrollmentListScopeLabel } from '@/utils/bookingScopeLabel';
import { Drawer } from '@/components/ui/Drawer';
import { Button } from '@/components/ui/Button';
import { EnrollStudentModal } from '@/components/schedule/EnrollStudentModal';
import { RemoveStudentEnrollmentDialog } from '@/components/lessons/RemoveStudentEnrollmentDialog';
import { EnrollmentConfirmButton } from '@/components/lessons/EnrollmentConfirmButton';
import { LessonShareTrigger } from '@/components/lessons/LessonShareTrigger';
import { lessonService, type LessonEnrollmentRow } from '@/services/lesson';
import { bookingService } from '@/services/booking';
import { useAgeGroups, usePrograms } from '@/hooks/useDataStore';
import { useManagementDashboardFeatureAccess } from '@/hooks/useFeatureAccess';
import {
  formatDateRange,
  getCampInstanceTrackLabel,
  isCampPast,
  resolveCampSessionDatesForEnroll,
} from './campDisplayUtils';

export interface CampInstanceStudentsDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  camp: Lesson | null;
  academyId: string;
  academySlug?: string;
  makeupLessonEnabled?: boolean;
  onChanged?: () => void | Promise<void>;
}

function formatSessionDateLabel(ymd: string | undefined, camp: Lesson): string {
  if (ymd) {
    try {
      return format(parse(ymd, 'yyyy-MM-dd', new Date()), 'EEE, MMM d, yyyy');
    } catch {
      return ymd;
    }
  }
  return formatDateRange(
    (camp as { startDate?: unknown }).startDate,
    (camp as { endDate?: unknown }).endDate
  );
}

export function CampInstanceStudentsDrawer({
  isOpen,
  onClose,
  camp,
  academyId,
  academySlug: _academySlug,
  makeupLessonEnabled = false,
  onChanged,
}: CampInstanceStudentsDrawerProps) {
  const { ageGroups } = useAgeGroups(academyId);
  const { hasAccess: hasProgramsAccess } = useManagementDashboardFeatureAccess(TierFeatureKey.PROGRAMS);
  const { programs: cachedPrograms } = usePrograms(academyId, {
    enabled: !!academyId && hasProgramsAccess,
  });
  const programs = hasProgramsAccess ? cachedPrograms : [];
  const [enrollments, setEnrollments] = useState<LessonEnrollmentRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [showEnrollModal, setShowEnrollModal] = useState(false);
  const [enrollModalKey, setEnrollModalKey] = useState(0);
  const [campSessionDates, setCampSessionDates] = useState<string[]>([]);
  const [campSessionDatesLoading, setCampSessionDatesLoading] = useState(false);
  const [removingBookingId, setRemovingBookingId] = useState<string | null>(null);
  const [confirmingBookingId, setConfirmingBookingId] = useState<string | null>(null);
  const [removeConfirm, setRemoveConfirm] = useState<{
    bookingId: string;
    studentName: string;
    isTermEnrollment: boolean;
    sessionDateYmd?: string;
  } | null>(null);
  const [selectedDayIndex, setSelectedDayIndex] = useState(0);

  const loadEnrollments = useCallback(async () => {
    if (!camp || !academyId) return;
    setLoading(true);
    try {
      const res = await lessonService.getLessonEnrollments(camp.id, { academyId });
      if (res.success && res.data) setEnrollments(res.data);
      else setEnrollments([]);
    } catch {
      setEnrollments([]);
      toast.error('Failed to load students for this camp');
    } finally {
      setLoading(false);
    }
  }, [camp, academyId]);

  useEffect(() => {
    if (isOpen && camp) {
      setShowEnrollModal(false);
      setRemoveConfirm(null);
      void loadEnrollments();
    }
  }, [isOpen, camp, loadEnrollments]);

  useEffect(() => {
    if (!isOpen || !camp) {
      setCampSessionDates([]);
      setCampSessionDatesLoading(false);
      return undefined;
    }
    let cancelled = false;
    setCampSessionDatesLoading(true);
    void resolveCampSessionDatesForEnroll(camp, (lessonId) =>
      lessonService.getLessonSessions(lessonId)
    )
      .then((dates) => {
        if (!cancelled) setCampSessionDates(dates);
      })
      .catch(() => {
        if (!cancelled) setCampSessionDates([]);
      })
      .finally(() => {
        if (!cancelled) setCampSessionDatesLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen, camp]);

  useEffect(() => {
    setSelectedDayIndex(0);
  }, [isOpen, campSessionDates]);

  const enrollmentByDay = useMemo(
    () => computeCampEnrollmentByDay(campSessionDates, enrollments),
    [campSessionDates, enrollments]
  );
  const enrollmentsByDay = useMemo(
    () => groupCampEnrollmentsByDay(campSessionDates, enrollments),
    [campSessionDates, enrollments]
  );
  const uniqueStudentCount = useMemo(() => countUniqueCampStudents(enrollments), [enrollments]);
  const lessonTrackLabel = useMemo(
    () => (camp ? getCampInstanceTrackLabel(camp, { ageGroups, programs }) : null),
    [camp, ageGroups, programs]
  );
  const maxCapacity = camp?.maxCapacity;
  const atCapacity =
    maxCapacity != null &&
    maxCapacity > 0 &&
    maxCapacity !== -1 &&
    (campSessionDates.length > 0
      ? isCampFullyBookedByDay(campSessionDates, enrollmentByDay, maxCapacity)
      : uniqueStudentCount >= maxCapacity);
  const campIsPast = camp != null && isCampPast(camp);
  const canAddStudent = camp != null && !atCapacity;

  const sessionDateLabel = useMemo(() => {
    if (!camp) return '';
    return formatDateRange(
      (camp as { startDate?: unknown }).startDate,
      (camp as { endDate?: unknown }).endDate
    );
  }, [camp]);

  const selectedDayData = useMemo(() => {
    if (campSessionDates.length === 0) return null;
    const totalDays = campSessionDates.length;
    const safeDayIndex = Math.min(selectedDayIndex, totalDays - 1);
    const currentDay = enrollmentsByDay[safeDayIndex];
    const sessionDateYmd = currentDay?.sessionDateYmd ?? campSessionDates[safeDayIndex];
    const dayEnrollments = currentDay?.enrollments ?? [];
    const dayCount = enrollmentByDay[sessionDateYmd] ?? 0;
    return {
      totalDays,
      safeDayIndex,
      sessionDateYmd,
      dayEnrollments,
      dayCount,
      dayLabel: formatSessionDateLabel(sessionDateYmd, camp!),
      capacitySuffix:
        maxCapacity != null && maxCapacity !== -1 ? ` / ${maxCapacity}` : '',
    };
  }, [
    campSessionDates,
    selectedDayIndex,
    enrollmentsByDay,
    enrollmentByDay,
    camp,
    maxCapacity,
  ]);

  const handleRemove = useCallback(
    async (
      bookingId: string,
      params: { scope: 'session' | 'term'; grantMakeupCredit: boolean },
      sessionDateYmd?: string
    ): Promise<boolean> => {
      setRemovingBookingId(bookingId);
      try {
        const res = await bookingService.removeEnrollment(bookingId, {
          academyId,
          scope: params.scope,
          sessionDate: params.scope === 'session' ? sessionDateYmd : undefined,
          grantMakeupCredit: params.grantMakeupCredit,
        });
        if (res.success) {
          toast.success(
            params.scope === 'term'
              ? 'Student removed from camp'
              : res.data?.makeupCreditGranted
                ? 'Student removed and make-up credit granted'
                : 'Student removed from camp day'
          );
          if (
            params.scope === 'session' &&
            params.grantMakeupCredit &&
            !res.data?.makeupCreditGranted &&
            res.data?.makeupCreditSkippedReason
          ) {
            toast.error(res.data.makeupCreditSkippedReason);
          }
          await loadEnrollments();
          await onChanged?.();
          return true;
        }
        toast.error((res as { error?: string }).error || 'Failed to remove student');
        return false;
      } catch (error: unknown) {
        toast.error(error instanceof Error ? error.message : 'Failed to remove student');
        return false;
      } finally {
        setRemovingBookingId(null);
      }
    },
    [academyId, loadEnrollments, onChanged]
  );

  const handleConfirmBooking = useCallback(
    async (bookingId: string) => {
      setConfirmingBookingId(bookingId);
      try {
        const res = await bookingService.updateBooking(bookingId, { status: 'confirmed' });
        if (res.success) {
          toast.success('Booking confirmed');
          await loadEnrollments();
          await onChanged?.();
        } else {
          toast.error((res as { error?: string }).error || 'Failed to confirm booking');
        }
      } catch (error: unknown) {
        toast.error(error instanceof Error ? error.message : 'Failed to confirm booking');
      } finally {
        setConfirmingBookingId(null);
      }
    },
    [loadEnrollments, onChanged]
  );

  if (!camp) return null;

  const renderEnrollmentRow = (row: LessonEnrollmentRow, dayYmd?: string) => {
    const scopeLabel = getCampEnrollmentListScopeLabel({
      bookingScope: row.bookingScope,
      sessionDateYmd: row.sessionDateYmd,
      status: row.status,
      enrollmentSource: row.enrollmentSource,
    });
    const isFullWeekRow =
      row.bookingScope === 'term' || row.bookingScope === 'package' || !row.sessionDateYmd;

    return (
      <li
        key={`${dayYmd ?? 'all'}-${row.bookingId ?? row.makeupSessionReservationId ?? row.studentId}`}
        className="flex flex-wrap items-center justify-between gap-2 border-b border-gray-100 pb-2 last:border-0 dark:border-gray-700"
      >
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
            <span className="font-medium text-gray-900 dark:text-white">{row.studentName}</span>
            {row.isMakeupBooking && (
              <span className="rounded bg-purple-100 px-1.5 py-0 text-[10px] text-purple-900 dark:bg-purple-900/50 dark:text-purple-100">
                Make-up
              </span>
            )}
            {row.status === 'pending' && (
              <span className="rounded bg-amber-100 px-1.5 py-0 text-[10px] text-amber-900 dark:bg-amber-900/50 dark:text-amber-100">
                Unconfirmed
              </span>
            )}
            {scopeLabel ? <span className="text-xs text-gray-500">{scopeLabel}</span> : null}
            {isFullWeekRow && dayYmd ? (
              <span className="text-xs text-gray-500">Full week</span>
            ) : null}
          </div>
          {shouldShowEnrollmentGuardian({
            isSelf: row.isSelf,
            isAdultStudent: row.isAdultStudent,
            isManual: row.isManual,
            hasLinkedGuardian: !!row.parentId,
          }) &&
            (row.parentName || row.parentEmail) && (
              <p className="mt-0.5 text-xs text-gray-500">
                Guardian: {row.parentName || '—'}
                {row.parentEmail ? ` · ${row.parentEmail}` : ''}
              </p>
            )}
        </div>
        {row.bookingId && (
          <div className="flex shrink-0 flex-wrap items-center gap-1">
            {row.status === 'pending' ? (
              <EnrollmentConfirmButton
                bookingId={row.bookingId}
                confirmingBookingId={confirmingBookingId}
                disabled={removingBookingId === row.bookingId}
                onConfirm={handleConfirmBooking}
              />
            ) : null}
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-8 shrink-0 gap-1.5 text-red-700 dark:text-red-300"
              disabled={
                removingBookingId === row.bookingId || confirmingBookingId === row.bookingId
              }
              onClick={() =>
                setRemoveConfirm({
                  bookingId: row.bookingId!,
                  studentName: row.studentName,
                  isTermEnrollment: isFullWeekRow,
                  sessionDateYmd: isFullWeekRow ? undefined : (row.sessionDateYmd ?? dayYmd),
                })
              }
            >
              {removingBookingId === row.bookingId ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                  …
                </>
              ) : (
                <>
                  <UserMinus className="h-4 w-4" aria-hidden />
                  Remove
                </>
              )}
            </Button>
          </div>
        )}
      </li>
    );
  };

  return (
    <>
      <Drawer
        isOpen={isOpen}
        onClose={onClose}
        title="Camp students"
        description={camp.name}
        width="lg"
        stackLevel="default"
        footer={
          <div className="flex w-full flex-wrap items-center justify-end gap-2">
            <LessonShareTrigger
              academyId={academyId}
              lessonId={camp.id}
              lessonName={camp.name}
              lessonType="camp"
              lessonIsActive={camp.isActive}
              enrollments={enrollments}
              onAfterNotify={loadEnrollments}
              stackLevel="default"
            />
            {canAddStudent ? (
              <Button
                type="button"
                className="w-full sm:w-auto gap-1.5"
                onClick={() => {
                  setEnrollModalKey((k) => k + 1);
                  setShowEnrollModal(true);
                }}
              >
                <UserPlus className="h-4 w-4" aria-hidden />
                Add student
              </Button>
            ) : null}
          </div>
        }
      >
        <div className="space-y-4 text-sm text-gray-800 dark:text-gray-200">
          <span className="inline-block rounded bg-gray-100 px-2 py-0.5 text-xs dark:bg-gray-700">
            {sessionDateLabel}
          </span>

          {campIsPast && (
            <p className="rounded-lg border border-amber-200 bg-amber-50/90 px-3 py-2 text-xs text-amber-950 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-100">
              This camp is in the past. You can still add students for record-keeping.
            </p>
          )}

          {atCapacity && !campIsPast && (
            <p className="rounded-lg border border-amber-200 bg-amber-50/90 px-3 py-2 text-xs text-amber-950 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-100">
              Every camp day is at capacity. Remove a student to add another, or increase max capacity
              in Edit.
            </p>
          )}

          <div>
            <h4 className="mb-2 flex items-center gap-2 font-medium text-gray-900 dark:text-white">
              <Users className="h-4 w-4" aria-hidden />
              Enrolled students
            </h4>
            {loading || campSessionDatesLoading ? (
              <p className="text-xs text-gray-500">Loading…</p>
            ) : selectedDayData ? (
              <section
                aria-labelledby="camp-day-enrollment-header"
                className="rounded-lg border border-gray-200 dark:border-gray-700"
              >
                <div className="flex items-center justify-between gap-2 border-b border-gray-200 bg-gray-50 px-2 py-2 dark:border-gray-700 dark:bg-gray-800/80 sm:px-3">
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-8 shrink-0 gap-1 px-2"
                    disabled={selectedDayData.safeDayIndex === 0}
                    aria-label="Previous camp day"
                    onClick={() => setSelectedDayIndex((i) => Math.max(0, i - 1))}
                  >
                    <ChevronLeft className="h-4 w-4" aria-hidden />
                    <span className="hidden sm:inline">Prev</span>
                  </Button>
                  <div
                    id="camp-day-enrollment-header"
                    aria-live="polite"
                    className="min-w-0 flex-1 text-center text-xs font-medium text-gray-900 dark:text-white"
                  >
                    <span className="block truncate">{selectedDayData.dayLabel}</span>
                    <span className="text-gray-500 dark:text-gray-400">
                      {selectedDayData.safeDayIndex + 1} of {selectedDayData.totalDays}
                      {selectedDayData.capacitySuffix
                        ? ` · ${selectedDayData.dayCount}${selectedDayData.capacitySuffix}`
                        : ''}
                    </span>
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-8 shrink-0 gap-1 px-2"
                    disabled={selectedDayData.safeDayIndex >= selectedDayData.totalDays - 1}
                    aria-label="Next camp day"
                    onClick={() =>
                      setSelectedDayIndex((i) =>
                        Math.min(selectedDayData.totalDays - 1, i + 1)
                      )
                    }
                  >
                    <span className="hidden sm:inline">Next</span>
                    <ChevronRight className="h-4 w-4" aria-hidden />
                  </Button>
                </div>
                {selectedDayData.dayEnrollments.length === 0 ? (
                  <p className="px-3 py-2 text-xs text-gray-500 dark:text-gray-400">
                    No students enrolled this day.
                  </p>
                ) : (
                  <ul className="max-h-[min(28rem,55vh)] space-y-2 overflow-y-auto px-3 py-2 pr-1">
                    {selectedDayData.dayEnrollments.map((row) =>
                      renderEnrollmentRow(row, selectedDayData.sessionDateYmd)
                    )}
                  </ul>
                )}
              </section>
            ) : enrollments.length === 0 && !canAddStudent ? (
              <p className="text-xs text-gray-500 dark:text-gray-400">No students enrolled yet.</p>
            ) : enrollments.length === 0 ? null : (
              <ul className="max-h-[min(24rem,50vh)] space-y-2 overflow-y-auto pr-1">
                {enrollments.map((row) => renderEnrollmentRow(row))}
              </ul>
            )}
          </div>

          {canAddStudent && !loading && !campSessionDatesLoading && enrollments.length === 0 && (
            <div className="rounded-lg border border-dashed border-gray-300 bg-muted/20 px-4 py-6 text-center dark:border-gray-600">
              <User className="mx-auto mb-2 h-8 w-8 text-gray-400" aria-hidden />
              <p className="mb-3 text-sm text-gray-600 dark:text-gray-400">
                Enroll from your student roster or add a manual student without a student or guardian
                account.
              </p>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="gap-1.5"
                onClick={() => {
                  setEnrollModalKey((k) => k + 1);
                  setShowEnrollModal(true);
                }}
              >
                <UserPlus className="h-4 w-4" aria-hidden />
                Add student
              </Button>
            </div>
          )}
        </div>
      </Drawer>

      <RemoveStudentEnrollmentDialog
        key={
          removeConfirm
            ? `remove-${removeConfirm.bookingId}-${removeConfirm.sessionDateYmd ?? 'full'}`
            : 'remove-closed'
        }
        isOpen={!!removeConfirm}
        studentName={removeConfirm?.studentName ?? ''}
        lessonName={camp.name}
        sessionDateLabel={formatSessionDateLabel(removeConfirm?.sessionDateYmd, camp)}
        sessionDateYmd={removeConfirm?.sessionDateYmd ?? ''}
        bookingId={removeConfirm?.bookingId ?? ''}
        makeupLessonEnabled={makeupLessonEnabled}
        isTermEnrollment={removeConfirm?.isTermEnrollment ?? false}
        isSubmitting={!!removeConfirm && removingBookingId === removeConfirm.bookingId}
        onClose={() => setRemoveConfirm(null)}
        onConfirm={async (params) => {
          if (!removeConfirm) return;
          const ok = await handleRemove(
            removeConfirm.bookingId,
            params,
            removeConfirm.sessionDateYmd
          );
          if (ok) setRemoveConfirm(null);
        }}
      />

      <EnrollStudentModal
        key={`camp-enroll-${camp.id}-${enrollModalKey}`}
        isOpen={showEnrollModal}
        onClose={() => setShowEnrollModal(false)}
        lessonId={camp.id}
        lessonName={camp.name}
        lessonTrackLabel={lessonTrackLabel}
        academyId={academyId}
        maxCapacity={maxCapacity}
        campEnrollmentByDay={enrollmentByDay}
        enrolledCount={uniqueStudentCount}
        stayOpenAfterEnroll
        campSessionDates={campSessionDates}
        campSessionDatesLoading={campSessionDatesLoading}
        showPastEnrollmentWarning={campIsPast}
        onEnrollSuccess={async () => {
          await loadEnrollments();
          await onChanged?.();
        }}
      />
    </>
  );
}
